// src/pages/roles/RoleForm.js

import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const RoleForm = ({ role, onClose, onSaveSuccess }) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const fetchedPermissions = await api.get('permissions');
        setAllPermissions(fetchedPermissions.data || fetchedPermissions);
      } catch (err) {
        console.error("Failed to fetch permissions for role form:", err);
        setError("Failed to load permissions for the form. " + (err.message || ""));
      }
    };
    fetchPermissions();

    if (role) {
      setIsEditing(true);
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || [],
      });
    }
  }, [role, api]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const currentPermissions = new Set(prev.permissions);
      if (checked) {
        currentPermissions.add(value);
      } else {
        currentPermissions.delete(value);
      }
      return { ...prev, permissions: Array.from(currentPermissions) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await api.put(`roles/${role._id}`, formData);
        alert("Role updated successfully!");
      } else {
        await api.post('roles', formData);
        alert("Role created successfully!");
      }
      onSaveSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "Operation failed.");
      console.error("Role form submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      <div>
        <label htmlFor="roleName" className="block text-sm font-medium text-gray-700">Role Name</label>
        <input
          type="text"
          name="name"
          id="roleName"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Optional)</label>
        <textarea
          name="description"
          id="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        ></textarea>
      </div>

      <div className="border border-gray-200 rounded-md p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Assign Permissions</label>
        {allPermissions.length === 0 && !loading && !error ? (
            <p className="text-sm text-gray-500">No permissions available. Add some in the Permissions section.</p>
        ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"> {/* Responsive grid */}
            {allPermissions.map((permission) => (
                <div key={permission._id} className="flex items-center">
                <input
                    type="checkbox"
                    id={`perm-${permission._id}`}
                    name="permissions"
                    value={permission._id}
                    checked={formData.permissions.includes(permission._id)}
                    onChange={handlePermissionChange}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label htmlFor={`perm-${permission._id}`} className="ml-2 text-sm text-gray-900">
                    {permission.name}
                </label>
                </div>
            ))}
            </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Role')}
        </button>
      </div>
    </form>
  );
};

export default RoleForm;
